import { CommonModule } from "@angular/common";
import { Component, computed, DestroyRef, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { OnlineIndicatorComponent } from "../../../../shared/components/online-indicator/online-indicator.component";
import { TabsModule } from "primeng/tabs";
import { LotCardComponent } from "./components/lot-card/lot-card.component";
import { CommunityLotDetail } from "./models/CommunityLotDetail";
import { AllMyLotsService } from "./services/all-my-lots.service";
import { CommunityDTO } from "./models/communityDTO";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { BottomSheetComponent } from "../../../../shared/components/bottom-sheet/bottom-sheet.component";
import { CmLotSearchComponent } from "../../../ccm/BQ7/lot-search/cm-search/cm-lot-search.component";
import { SvgButtonComponent } from "../../../../shared/components/search-button/svg-search-button.component";
import { ActivatedRoute, Router } from "@angular/router";
import { CmLotService } from "./services/cm-lot.service";
import { ConstructionManagerScheduleService } from "../../../../domains/jobs/services/construction-manager-schedule.service";
import { BackgroundRefreshCoordinator } from "../../../../shared/utils/background-refresh-coordinator";
import { BackgroundRefreshIndicatorComponent } from "../../../../shared/components/background-refresh-indicator/background-refresh-indicator.component";
import { DataUpdatedFadeDirective } from "../../../../shared/directives/data-updated-fade.directive";
import { FeatureService } from "../../../../core/feature-flags/feature.service";
import { AdminConfigKey, LotPrimaryIdentifier } from "../../../../core/feature-flags/models/AdminConfigDto";
import { SkeletonModule } from "primeng/skeleton";


@Component({
  standalone: true,
  selector: "app-all-my-lots",
  imports: [
    OnlineIndicatorComponent,
    TabsModule,
    CommonModule,
    LotCardComponent,
    BottomSheetComponent,
    CmLotSearchComponent,
    SvgButtonComponent,
    BackgroundRefreshIndicatorComponent,
    DataUpdatedFadeDirective,
    SkeletonModule
  ],
  templateUrl: "./all-my-lots.component.html",
  styleUrl: "./all-my-lots.component.scss",
  schemas: []
})
export class AllMyLotsComponent implements OnInit, OnDestroy {
  allMyLots: CommunityDTO[] = [];
  tabList: { tabIndex: number; title: string; lots: CommunityLotDetail[] }[] = [];
  isBottomSheetOpen = false;
  isSearchFullScreen = false;
  newlyAddedLotKeys = new Set<string>();
  private highlightTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private backgroundRefreshCoordinator?: BackgroundRefreshCoordinator;
  readonly isRefreshingInBackground = computed(() => this.constructionManagerScheduleService.isFetchingSchedule());
  dataUpdatedPulse = signal(false);

  selectedIndex: number = 0;

  lotPrimaryIdentifier: LotPrimaryIdentifier = LotPrimaryIdentifier.LotBlock;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private allMyLotsService: AllMyLotsService,
    private toastService: UiToastService,
    private route: ActivatedRoute,
    private router: Router,
    private cmLotService: CmLotService,
    private constructionManagerScheduleService: ConstructionManagerScheduleService,
    private featureService: FeatureService
  ) { }

  async ngOnInit(): Promise<void> {
    this.selectedIndex = Number(this.route.snapshot.paramMap.get("communityId") ?? "0");
    this.lotPrimaryIdentifier = (await this.featureService.getEffectiveConfig(AdminConfigKey.LotPrimaryIdentifier)) ?? LotPrimaryIdentifier.LotBlock;

    // Subscribes to the CM schedule's background-refresh notification before the initial load runs, so
    // a refresh that lands mid-load is never silently missed, and re-reads local data on every later one.
    this.backgroundRefreshCoordinator = new BackgroundRefreshCoordinator(
      this.constructionManagerScheduleService.scheduleRefreshed,
      async redirectOnMissingData => {
        await this.loadAllMyLots();

        // Tab selection/navigation is only meaningful for the initial load, not a background refresh.
        if (redirectOnMissingData) {
          const match = this.tabList.find(t => t.tabIndex === this.selectedIndex);
          if (!match && this.tabList.length > 0) {
            this.onTabChange(this.tabList[0].tabIndex);
          } else {
            this.onTabChange(match ? match.tabIndex : 0);
          }
        } else {
          this.dataUpdatedPulse.set(true);
        }

        return true;
      },
      this.destroyRef
    );

    await this.backgroundRefreshCoordinator.runInitialLoad();
  }

  ngOnDestroy(): void {
    if (this.highlightTimeoutId) {
      clearTimeout(this.highlightTimeoutId);
    }
  }

  private async loadAllMyLots(): Promise<void> {
    this.allMyLots = await this.allMyLotsService.getAllMyLots();
    this.rebuildTabList();
  }

  getLotItems(communityId: number, lotId: number): CommunityLotDetail {
    const community = this.allMyLots.find(c => c.externalFinancialCommunityId === communityId);
    const lot = community?.lots.find(l => l.id === lotId);

    if (!lot) {
      this.toastService.error(`Lot with id ${lotId} not found in community ${communityId}`);
      return {} as CommunityLotDetail;
    }

    return lot;
  }

  focusTab(element: HTMLElement): void {
    element.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }

  showIndicatorBottomSheet(): void {
    this.isBottomSheetOpen = true;
    this.isSearchFullScreen = false;
  }

  onSheetModeChange(mode: "sheet" | "fullscreen"): void {
    this.isSearchFullScreen = mode === "fullscreen";
  }

  onTabRowWheel(event: WheelEvent): void {
    const target = event.currentTarget as HTMLElement;
    const tabViewport = target?.querySelector(".p-tablist-viewport");
    if (tabViewport) {
      const hasHorizontalOverflow = tabViewport.scrollWidth > tabViewport.clientWidth;
      if (hasHorizontalOverflow) {
        event.preventDefault();
        tabViewport.scrollLeft += (event.deltaY * 1.2);
      }
    }
  }

  async closeSearchSheet(): Promise<void> {
    const previousLotKeys = this.buildLotKeySet(this.allMyLots);

    this.isBottomSheetOpen = false;
    this.isSearchFullScreen = false;

    const refreshedLots = await this.allMyLotsService.getAllMyLots();
    const nextLotKeys = this.buildLotKeySet(refreshedLots);

    const newlyAddedLotKeys = new Set<string>();

    nextLotKeys.forEach(key => {
      if (!previousLotKeys.has(key)) {
        newlyAddedLotKeys.add(key);
      }
    });

    this.allMyLots = refreshedLots;
    this.rebuildTabList();
    this.newlyAddedLotKeys = newlyAddedLotKeys;

    const firstNewLot = this.findFirstNewLotCommunityId();
    if (firstNewLot !== null) {
      this.selectedIndex = firstNewLot;
    }

    if (this.highlightTimeoutId) {
      clearTimeout(this.highlightTimeoutId);
    }

    this.highlightTimeoutId = setTimeout(() => {
      this.newlyAddedLotKeys.clear();
    }, 4000);
  }

  isNewlyAddedLot(communityId: number, lotId: number): boolean {
    return this.newlyAddedLotKeys.has(this.getLotKey(communityId, lotId));
  }

  async onArchiveLot(lotId: number): Promise<void> {
    const success = await this.cmLotService.archiveLot(lotId);
    if (success) {
      await this.loadAllMyLots();
    }
  }

  onTabChange(index: string | number): void {
    this.selectedIndex = Number(index);
    this.router.navigate(["/cm/all-my-lots/community", this.selectedIndex], {
      replaceUrl: true
    });
  }

  private rebuildTabList(): void {
    this.tabList = this.allMyLots.map(c => ({
      tabIndex: c.externalFinancialCommunityId,
      title: c.financialCommunityName ?? c.salesCommunityName,
      lots: c.lots
    }));

    setTimeout(() => {
      document.querySelector(".p-tab-active")?.scrollIntoView();

      setTimeout(() => {
        document.querySelector(".p-tab-active")?.scrollIntoView();
      }, 50);
    });
  }

  private buildLotKeySet(communities: CommunityDTO[]): Set<string> {
    return new Set(
      communities.flatMap(community =>
        community.lots.map(lot => this.getLotKey(community.externalFinancialCommunityId, lot.id))
      )
    );
  }

  private getLotKey(communityId: number, lotId: number): string {
    return `${communityId}:${lotId}`;
  }

  private findFirstNewLotCommunityId(): number | null {
    for (const tab of this.tabList) {
      const hasNewLot = tab.lots.some(lot => this.isNewlyAddedLot(tab.tabIndex, lot.id));
      if (hasNewLot) {
        return tab.tabIndex;
      }
    }

    return null;
  }
}